import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../lib/auth-context';
import { userRoleAPI, configAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Phone,
  Globe,
  Shield,
  Bell,
  Languages,
  ChevronRight,
  LogOut,
  Settings,
  CreditCard,
  Car,
  Key,
  Plus,
  CheckCircle2
} from 'lucide-react';

const SettingItem = ({ icon: Icon, title, description, action, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-slate-50 transition-colors text-left"
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-slate-500" />
      </div>
      <div>
        <p className="text-slate-800 font-medium">{title}</p>
        {description && <p className="text-slate-500 text-sm">{description}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2 text-slate-400">
      {action && <span className="text-sm">{action}</span>}
      <ChevronRight className="w-5 h-5" />
    </div>
  </button>
);

const RoleCard = ({ role, active, canActivate, onActivate }) => {
  const roleConfig = {
    passenger: { icon: User, name: 'Pasajero', description: 'Solicita viajes, rentas y envíos', color: '#00BFA6' },
    driver: { icon: Car, name: 'Conductor', description: 'Acepta viajes y entregas', color: '#FFD166' },
    vehicle_owner: { icon: Key, name: 'Dueño de Vehículo', description: 'Renta tus vehículos', color: '#00887A' },
  };

  const config = roleConfig[role] || roleConfig.passenger;
  const Icon = config.icon;

  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${
      active 
        ? 'border-[#00BFA6] bg-[#00BFA6]/5' 
        : 'border-slate-200 bg-white hover:border-slate-300'
    }`}>
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${config.color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color: config.color }} />
        </div>
        <div className="flex-1">
          <p className="text-slate-800 font-medium">{config.name}</p>
          <p className="text-slate-500 text-xs">{config.description}</p>
        </div>
        {active && (
          <CheckCircle2 className="w-5 h-5 text-[#00BFA6]" />
        )}
      </div>
      {!active && canActivate && (
        <Button
          onClick={() => onActivate(role)}
          className="w-full h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Activar este rol
        </Button>
      )}
    </div>
  );
};

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const [language, setLanguage] = useState('es');
  const [country, setCountry] = useState(user?.country || 'MX');
  const [countries, setCountries] = useState([]);
  const [userRoles, setUserRoles] = useState({ primary_role: user?.role, all_roles: [user?.role], can_activate: [] });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [countriesRes, rolesRes] = await Promise.all([
        configAPI.getCountries(),
        userRoleAPI.getRoles().catch(() => ({ data: { all_roles: [user?.role], can_activate: [] } }))
      ]);
      setCountries(countriesRes.data);
      setUserRoles(rolesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleActivateRole = async (role) => {
    try {
      await userRoleAPI.activateRole(role);
      toast.success(`¡Rol de ${role === 'driver' ? 'Conductor' : 'Dueño de Vehículo'} activado!`);
      refreshUser();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar rol');
    }
  };

  const handleLogout = () => {
    logout();
    toast.info('Sesión cerrada');
  };

  const roleLabels = {
    passenger: 'Pasajero',
    driver: 'Conductor',
    vehicle_owner: 'Dueño de vehículo',
    admin: 'Administrador',
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 animate-fade-in">
        {/* Profile Header */}
        <Card className="card-elevated overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-[#00BFA6]/20 to-[#00887A]/20" />
          <CardContent className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
              <div className="w-24 h-24 rounded-2xl bg-[#00BFA6] flex items-center justify-center border-4 border-white shadow-xl">
                <User className="w-12 h-12 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-800 font-['Outfit']">{user?.full_name}</h1>
                <p className="text-slate-500">{user?.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(userRoles.all_roles || [user?.role]).map((role) => (
                    <span key={role} className="px-3 py-1 bg-[#00BFA6]/10 text-[#00BFA6] text-sm font-medium rounded-full">
                      {roleLabels[role] || role}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Multi-Role Section */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00BFA6]" />
              Mis Roles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-500 text-sm mb-4">
              Puedes tener múltiples roles con una sola cuenta. Tu wallet y historial se mantienen unificados.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <RoleCard
                role="passenger"
                active={userRoles.all_roles?.includes('passenger')}
                canActivate={userRoles.can_activate?.includes('passenger')}
                onActivate={handleActivateRole}
              />
              <RoleCard
                role="driver"
                active={userRoles.all_roles?.includes('driver')}
                canActivate={userRoles.can_activate?.includes('driver')}
                onActivate={handleActivateRole}
              />
              <RoleCard
                role="vehicle_owner"
                active={userRoles.all_roles?.includes('vehicle_owner')}
                canActivate={userRoles.can_activate?.includes('vehicle_owner')}
                onActivate={handleActivateRole}
              />
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-[#00BFA6]" />
              Información de la cuenta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Nombre completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={user?.full_name || ''}
                    readOnly
                    className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                    data-testid="profile-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={user?.email || ''}
                    readOnly
                    className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                    data-testid="profile-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={user?.phone || ''}
                    readOnly
                    className="pl-11 h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"
                    data-testid="profile-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">País</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="h-12 bg-slate-50 border-slate-200 text-slate-800 rounded-xl" data-testid="profile-country">
                    <Globe className="w-5 h-5 mr-2 text-slate-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code} className="text-slate-800">
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#00BFA6]" />
              Configuración
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <SettingItem icon={Languages} title="Idioma" description="Idioma de la aplicación" action="Español" />
            <SettingItem icon={Globe} title="Moneda" description="Moneda preferida" action="USD" />
            <SettingItem icon={Bell} title="Notificaciones" description="Gestionar notificaciones push" action="Activadas" />
            <SettingItem icon={CreditCard} title="Métodos de pago" description="Gestionar métodos de pago" />
            <SettingItem icon={Shield} title="Seguridad" description="Contraseña y verificación" />
          </CardContent>
        </Card>

        {/* Wallet Summary */}
        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00BFA6]/15 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#00BFA6]" />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Balance total</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${((user?.wallet_balance || 0) + (user?.diggii_balance || 0)).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-slate-500 text-sm">USD: ${user?.wallet_balance?.toFixed(2)}</p>
                <p className="text-[#FFD166] text-sm font-medium">DIGGII: {user?.diggii_balance?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full h-14 border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Cerrar sesión
        </Button>

        {/* Footer */}
        <div className="text-center text-slate-400 text-sm py-4">
          <p>Mercapoli Move v1.0.0</p>
          <p className="mt-1">© 2025 Todos los derechos reservados</p>
        </div>
      </div>
    </Layout>
  );
}
